import { Fragment, ReactElement } from "react";
import { Divider, Paper, useTheme } from "@mui/material";
import Chart from "../../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../../components/charts/AxisHorizontal";
import { Alloy_Tolerance, IKPIRecordData } from "../types";
import { formatNumber } from "../../../../system/utils";
import HtmlTooltip from "../../../../components/charts/tooltip";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Grid from "../../../../components/charts/grid";
import AxisVertical from "../../../../components/charts/AxisVertical";

interface IProps {
  height: number;
  groups: string[];
  records: IKPIRecordData[];
}

const paddingInner = 0; // .1;
const paddingOuter = 0; // .1;
const align = 0.5;

const AdditionsChart3 = ({ height, records }: IProps): ReactElement => {
  const theme = useTheme();
  const barID = useUniqueId("stacked-bar-chart");
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height,
    marginBottom: 55,
    marginLeft: 60,
    marginRight: 60,
    marginTop: 40
  });

  const xScale = d3.scaleBand()
    .domain(records.map(r => r.material))
    .range([0, dimensions.boundedWidth])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter)
    .align(align);

  const yScale = d3.scaleLinear()
    .domain([0, 100])
    .range([dimensions.boundedHeight, 0]);

  const yScaleDiff = d3.scaleLinear()
    .domain([-100, 100])
    .range([dimensions.boundedHeight, 0])
    .nice();

  const xTicks = xScale.domain();
  const yTicks = getTicks(yScale.domain() as number[], 5);
  const yTicksDiff = getTicks(yScaleDiff.domain() as number[], 5);
  const groupWidth = xScale.bandwidth() - 20;

  const lineGenerator = d3.line<IKPIRecordData>()
    .x(d => (xScale(d.material) ?? 0) + xScale.bandwidth() / 2)
    .y(d => yScaleDiff(d.delta * 100 / Math.max(d.totalSuggested, 1)))
    .curve(d3.curveLinear);


  return (
    <Paper ref={ref}  style={{ height }}>
      <Chart dimensions={dimensions} >
        <defs>
          <clipPath id="chart-cut-off">
            <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
          </clipPath>
        </defs>

        <g>
          <AxisHorizontal
            key={`${barID}-x-axis`}
            chartID={barID}
            dimensions={dimensions}
            scale={xScale}
            label="Additions"
            ticks={xTicks}
            marginTop={10}
          />

          <AxisVertical
            chartID={barID}
            dimensions={dimensions}
            scale={yScale}
            ticks={yTicks}
            label="lb"
            formatTick={(s: any) => s.toFixed(0)}/>

          <AxisVertical
            chartID={barID}
            dimensions={dimensions}
            scale={yScaleDiff}
            ticks={yTicksDiff}
            label="% Diff"
            side="LEFT"
            formatTick={(s: any) => s.toFixed(0)}/>
        </g>

        <g>
          { records.map((record, rIndex) => {
            const xBase = rIndex * 225;
            let suggestedValue = 0;
            let realValue = 0;

            Object.values(record.groups).map(group => {
              suggestedValue += group.suggestedValue.sum();
              realValue += group.weight.sum();
            });

            const diff = realValue - suggestedValue;
            const ok = suggestedValue * Alloy_Tolerance > Math.abs(diff);

            return (
              <Fragment key={`legend-${rIndex}`}>
                <rect x={xBase} y={-34} width={220} height={28} rx={4} fill="#777" fillOpacity={.5} />
                <circle r={8} fill={record.color} cy={-20} cx={xBase + 12}/>
                <text x={xBase + 24} y={-19} dominantBaseline="middle"
                  fontWeight="semi-bold" fill={theme.palette.text.primary}
                  className="Axis__label">
                  {record.material}:
                </text>
                <text x={xBase + 192} y={-19} dominantBaseline="middle"
                  fontWeight="semi-bold" fill={theme.palette.text.primary}
                  className="Axis__label" textAnchor="end">
                  {`${diff.toFixed(2)} lb`}
                </text>
                { ok &&
                  <path fill="green" stroke="green" strokeLinecap="round" strokeLinejoin="round"
                    transform={`translate(${xBase + 192}, -33)`}
                    d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                }
                { !ok && diff > 0 &&
                  <path fill="red" stroke="red" strokeLinecap="round" strokeLinejoin="round"
                    transform={`translate(${xBase + 192}, -33)`}
                    d="m4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" />
                }
                { !ok && diff < 0 &&
                  <path fill="red" stroke="red" strokeLinecap="round" strokeLinejoin="round"
                    transform={`translate(${xBase + 192}, -33)`}
                    d="m20 12-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z" />
                }
              </Fragment>
            );
          })}
        </g>

        <Grid charID={barID} xScale={xScale} yScale={yScale} dimensions={dimensions}  />

        <g>
          { records.map((record, rIndex) => {
            const leftBase = (xScale(record.material) ?? 0) + 10;
            const total = record.totalReal + record.totalSuggested;
            const factor = total > 0 ? 100.0 / total : 0.0;
            let suggestedHeight = 0;
            let realHeight = 0;

            let suggestedY = 1;
            let realY = 1;

            const list = Object.entries(record.groups);
            const totalGroups = list.length;
            const factorOpacity = 1 / (totalGroups + 1);

            return (
              <Fragment key={`g-${rIndex}}`}>
                {list.map(([keyGroup, group], gIndex) => {
                  const fillOpacity = factorOpacity * (totalGroups - gIndex + 1);
                  const textMiddleX = leftBase + groupWidth / 2 + 10;
                  const suggestedValue = group.suggestedValue.sum();
                  const realValue = group.weight.sum();
                  const diff = realValue - suggestedValue;

                  suggestedY += suggestedHeight;
                  realY += realHeight;
                  suggestedHeight = dimensions.boundedHeight - yScale(suggestedValue * factor);
                  realHeight = dimensions.boundedHeight - yScale(realValue * factor);


                  let icon = <CheckIcon color="success" />;
                  if (suggestedValue * Alloy_Tolerance < Math.abs(diff)) {
                    icon = diff > 0 ? <ArrowUpwardIcon color="error" /> : <ArrowDownwardIcon color="error" />;
                  }

                  return (
                    <Fragment key={`g-${rIndex}-${gIndex}`}>
                      <HtmlTooltip
                        title={
                          <div className="display-flex-column">
                            <strong className="flex-grow-1 mr-2 align-self-center">{keyGroup}</strong>
                            <Divider/>
                            <div className="display-flex-row gap-1 align-items-center">
                              <div className="display-flex-column flex-grow-1">
                                <div className="display-flex-row align-items-center" >
                                  <strong className="flex-grow-1 mr-2">Spec</strong>
                                  <strong className="flex-grow-0">
                                    { formatNumber(suggestedValue, 2) }
                                  </strong>
                                </div>
                                <div className="display-flex-row align-items-center" >
                                  <strong className="flex-grow-1 mr-2">Real</strong>
                                  <strong className="flex-grow-0">
                                    { formatNumber(realValue, 2) }
                                  </strong>
                                </div>
                              </div>
                              <div className="display-flex-row gap-1">
                                {icon}
                                { formatNumber(diff, 2) }
                              </div>
                            </div>
                          </div>
                        }>
                        <g>
                          <rect
                            className="Bars__rect"
                            fill={record.color}
                            fillOpacity={fillOpacity}
                            height={suggestedHeight}
                            width={groupWidth}
                            y={dimensions.boundedHeight - suggestedY - suggestedHeight}
                            x={leftBase}/>

                          { suggestedHeight > 15 &&
                            <text x={textMiddleX} textAnchor="middle" dominantBaseline="middle"
                              y={dimensions.boundedHeight - suggestedY - suggestedHeight / 2} fontSize="0.9em">
                              {keyGroup}
                            </text>
                          }

                          <rect
                            className="Bars__rect"
                            fill={record.color}
                            fillOpacity={fillOpacity}
                            height={realHeight}
                            width={groupWidth}
                            y={realY}
                            x={leftBase}
                          />
                          { realHeight > 15 &&
                            <text x={textMiddleX} textAnchor="middle" dominantBaseline="middle"
                              y={realY + realHeight / 2} fontSize="0.9em">
                              {keyGroup}
                            </text>
                          }
                        </g>
                      </HtmlTooltip>
                    </Fragment>
                  );
                })}
                <line x1={leftBase} x2={leftBase + groupWidth}
                  y1={realY + realHeight - 1} y2={realY + realHeight - 1}
                  stroke="whitesmoke" strokeWidth={1} strokeOpacity={.5} />
              </Fragment>
            );
          })}
        </g>

        <g clipPath="url(#chart-cut-off)">
          <line x1={10} x2={dimensions.boundedWidth - 10} y1={dimensions.boundedHeight / 2} y2={dimensions.boundedHeight / 2} stroke="#0a0" strokeWidth={1} />
          <path
            className={"Line"}
            fill="none"
            stroke="red"
            strokeWidth={2}
            d={lineGenerator(records) ?? ""}
          />
          { records.map((record, rIndex) => {
            const leftBase = xScale(record.material) ?? 0; // rIndex * xScale.bandwidth();

            return (
              <circle key={`circle-delta-${rIndex}`} r={4} fill="red" cx={leftBase + xScale.bandwidth() / 2}
                stroke="whitesmoke" strokeWidth={1}
                cy={yScaleDiff(record.delta * 100 / Math.max(record.totalSuggested, 1))} />
            );
          })}
        </g>

      </Chart>
    </Paper>
  );
};

export default AdditionsChart3;